import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Row, Col, Input, List, Badge, Avatar, Button, Typography, Space, Empty, Tag, message as antMessage, Spin } from 'antd';
import { SendOutlined, UserOutlined, SearchOutlined, BellOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { beepMessage, buzz } from '../utils/beep';

const { Text, Title } = Typography;

// Live chat between admin and staff. Admin picks a person and talks right away;
// the other side gets a full-page popup + beep + email (see api/chat.php).
export default function LiveChat({ user }) {
  const [people, setPeople] = useState([]);
  const [peer, setPeer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastIdRef = useRef(0);
  const bottomRef = useRef(null);
  const peerRef = useRef(null);

  const loadPeople = useCallback(async () => {
    try {
      const res = await api.get('/chat/people');
      setPeople(Array.isArray(res.data) ? res.data : []);
    } catch (err) { /* ignore */ }
    setLoading(false);
  }, []);

  const loadMessages = useCallback(async (peerId, reset) => {
    if (!peerId) return;
    try {
      const after = reset ? 0 : lastIdRef.current;
      const res = await api.get('/chat/messages', { params: { peer_id: peerId, after_id: after } });
      const rows = Array.isArray(res.data) ? res.data : [];
      if (rows.length > 0) {
        lastIdRef.current = Number(rows[rows.length - 1].id);
        const incoming = rows.some(r => Number(r.from_user_id) !== Number(user.id));
        setMessages(prev => (reset ? rows : [...prev, ...rows]));
        if (incoming && !reset) { beepMessage(); buzz(); }
      } else if (reset) {
        setMessages([]);
      }
    } catch (err) { /* ignore */ }
  }, [user.id]);

  useEffect(() => { loadPeople(); }, [loadPeople]);

  useEffect(() => {
    peerRef.current = peer;
    if (!peer) return;
    lastIdRef.current = 0;
    loadMessages(peer.id, true);
  }, [peer, loadMessages]);

  useEffect(() => {
    const t = setInterval(() => {
      if (peerRef.current) loadMessages(peerRef.current.id, false);
      loadPeople();
    }, 4000);
    return () => clearInterval(t);
  }, [loadMessages, loadPeople]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    const body = text.trim();
    if (!body || !peer) return;
    setSending(true);
    try {
      await api.post('/chat/messages', { to_user_id: peer.id, message: body });
      setText('');
      await loadMessages(peer.id, false);
      loadPeople();
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'Message bhej nahi paye');
    }
    setSending(false);
  };

  const ring = async () => {
    if (!peer) return;
    try {
      const res = await api.post('/chat/invite', { to_user_id: peer.id });
      antMessage.success(res.data?.emailed
        ? `${peer.full_name} ko notification + email bhej diya`
        : `${peer.full_name} ko notification bhej diya`);
      loadMessages(peer.id, false);
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'Request bhej nahi paye');
    }
  };

  const filtered = people.filter(p =>
    !search || (p.full_name || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <Title level={3} style={{ marginBottom: 16 }}>Live Chat</Title>
      <Row gutter={16}>
        <Col xs={24} md={8} lg={7}>
          <Card
            size="small"
            title={`People (${people.length})`}
            extra={<Button size="small" type="text" icon={<ReloadOutlined />} onClick={loadPeople} />}
            styles={{ body: { padding: 8 } }}
          >
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search name"
              value={search}
              onChange={e => setSearch(e.target.value)}
              allowClear
              style={{ marginBottom: 8 }}
            />
            {loading ? <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div> : (
              <div style={{ maxHeight: 460, overflowY: 'auto' }}>
                <List
                  size="small"
                  dataSource={filtered}
                  locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Koi user nahi" /> }}
                  renderItem={p => (
                    <List.Item
                      onClick={() => setPeer(p)}
                      style={{
                        cursor: 'pointer',
                        borderRadius: 8,
                        padding: '8px 10px',
                        background: peer?.id === p.id ? '#eef0ff' : undefined
                      }}
                    >
                      <List.Item.Meta
                        avatar={
                          <Badge dot status={p.online ? 'success' : 'default'} offset={[-4, 30]}>
                            <Avatar style={{ background: p.online ? '#52c41a' : '#bbb' }} icon={<UserOutlined />} />
                          </Badge>
                        }
                        title={
                          <Space size={6}>
                            <Text strong style={{ fontSize: 13 }}>{p.full_name}</Text>
                            {p.unread > 0 && <Badge count={p.unread} size="small" />}
                          </Space>
                        }
                        description={
                          <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                            {p.last_message ? p.last_message.slice(0, 34) : p.role}
                          </Text>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} md={16} lg={17}>
          {!peer ? (
            <Card><Empty description="Kisi bhi naam par click karke live chat shuru karo" /></Card>
          ) : (
            <Card
              size="small"
              title={
                <Space>
                  <Avatar size="small" style={{ background: peer.online ? '#52c41a' : '#bbb' }} icon={<UserOutlined />} />
                  <Text strong>{peer.full_name}</Text>
                  <Tag color={peer.online ? 'green' : 'default'}>{peer.online ? 'Online' : 'Offline'}</Tag>
                </Space>
              }
              extra={
                <Button icon={<BellOutlined />} onClick={ring} type="primary" ghost size="small">
                  Chat request + email
                </Button>
              }
              styles={{ body: { padding: 12 } }}
            >
              <div style={{ height: 400, overflowY: 'auto', padding: '4px 8px', background: '#fafbff', borderRadius: 8 }}>
                {messages.length === 0 && (
                  <div style={{ textAlign: 'center', paddingTop: 140 }}>
                    <Text type="secondary">Koi message nahi — pehla message bhejo</Text>
                  </div>
                )}
                {messages.map(m => {
                  const mine = Number(m.from_user_id) === Number(user.id);
                  return (
                    <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                      <div style={{
                        maxWidth: '72%',
                        background: mine ? '#4f46e5' : '#fff',
                        color: mine ? '#fff' : '#222',
                        border: mine ? 'none' : '1px solid #e6e8f0',
                        borderRadius: 12,
                        padding: '8px 12px',
                        boxShadow: '0 1px 4px rgba(60,70,120,0.08)'
                      }}>
                        {Number(m.is_invite) === 1 && (
                          <Tag color="purple" style={{ marginBottom: 4 }}>Live chat request</Tag>
                        )}
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13 }}>{m.message}</div>
                        <div style={{ fontSize: 10, opacity: 0.7, textAlign: 'right', marginTop: 2 }}>
                          {dayjs(m.created_at).format('DD MMM, hh:mm A')}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              <Space.Compact style={{ width: '100%', marginTop: 10 }}>
                <Input
                  placeholder="Message likho..."
                  value={text}
                  onChange={e => setText(e.target.value)}
                  onPressEnter={send}
                  disabled={sending}
                />
                <Button type="primary" icon={<SendOutlined />} onClick={send} loading={sending}>Send</Button>
              </Space.Compact>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Offline hone par unhe email bhi chala jaata hai. Message aane par dono taraf beep bajega.
              </Text>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
